import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography, { TypographyProps } from "@insite/mobius/Typography/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CustomApplicationState from "@tKgpco/Store/CustomApplicationState";
import React, { FC, useEffect, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

export interface EmbeddedChatbotStyles {
    container?: InjectableCss;
    iframeWrapper?: InjectableCss;
    noResultsContainer?: InjectableCss;
    noResultsText?: TypographyProps;
}

const mapStateToProps = (state: CustomApplicationState) => ({
    customerNumber: getBillToState(state, state.context.session.billToId).value?.customerNumber,
    featurePages: state.context.session.properties.featurePages,
});

type Props = WidgetProps & ReturnType<typeof mapStateToProps>;

export const embeddedChatbotStyles: EmbeddedChatbotStyles = {
    container: {
        css: css`
            width: 100%;
            height: 100%;
            display: flex;
            flex-direction: column;
        `,
    },
    iframeWrapper: {
        css: css`
            width: 100%;
            height: 85vh;
            min-height: 600px;
            border: 1px solid #ddd;
            border-radius: 8px;
            overflow: hidden;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

            iframe {
                width: 100%;
                height: 100%;
                border: none;
                display: block;
            }

            @media (max-width: 768px) {
                height: 70vh;
                min-height: 500px;
            }
        `,
    },
    noResultsContainer: {
        css: css`
            text-align: center;
            padding: 20px;
        `,
    },
    noResultsText: {
        variant: "h4",
    },
};

const styles = embeddedChatbotStyles;

const chatbotProxyPath = "/api/v2/chatbot/proxy/customer-chat";

export const buildChatbotProxyUrl = (customerNumber: string) =>
    `${chatbotProxyPath}?CustomerID=${encodeURIComponent(customerNumber)}`;

const EmbeddedChatbot: FC<Props> = ({ customerNumber, featurePages }) => {
    const [iframeUrl, setIframeUrl] = useState<string>("");

    useEffect(() => {
        if (!customerNumber) {
            setIframeUrl("");
            return;
        }

        setIframeUrl(buildChatbotProxyUrl(customerNumber));
    }, [customerNumber]);

    if (featurePages?.includes("Chatbot")) {
        return (
            <StyledWrapper {...styles.noResultsContainer}>
                <Typography as="p" {...styles.noResultsText} data-test-selector="no-access-message">
                    {translate(`You don’t have permission to view this page`)}
                </Typography>
            </StyledWrapper>
        );
    }

    if (!iframeUrl) {
        return null;
    }

    return (
        <StyledWrapper {...styles.container}>
            <StyledWrapper {...styles.iframeWrapper}>
                <iframe
                    src={iframeUrl}
                    title="AI Chatbot"
                    allow="clipboard-read; clipboard-write; microphone"
                    sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox"
                />
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(EmbeddedChatbot),
    definition: {
        group: "Common",
        displayName: "Embedded Chatbot",
    },
};

export default widgetModule;
